import React from 'react';
import { motion } from 'framer-motion';
import Analytics from '../../services/analytics';

const testimonials = [
  {
    quote: "AgentSpot has transformed how our support team operates. Our AI agents handle 70% of routine questions, freeing our team to focus on complex issues.",
    author: "Sarah Johnson",
    title: "Head of Customer Support, TechGrowth",
    image: "https://randomuser.me/api/portraits/women/1.jpg"
  },
  {
    quote: "Setting up AI agents across our Slack and email channels took less than an hour, and the impact was immediate. Our team saves 15+ hours per week.",
    author: "Michael Chen",
    title: "CTO, FastLaunch",
    image: "https://randomuser.me/api/portraits/men/2.jpg"
  },
  {
    quote: "The no-code approach makes it incredibly accessible. Everyone on our team can customize agents for their workflow needs without IT help.",
    author: "Priya Sharma",
    title: "Operations Manager, ScaleUp",
    image: "https://randomuser.me/api/portraits/women/3.jpg"
  },
];

interface TestimonialsSectionProps {
  openSignupModal: (itemName: string) => void;
}

const TestimonialsSection: React.FC<TestimonialsSectionProps> = ({ openSignupModal }) => {
  const handleGetStarted = () => {
    Analytics.trackButtonClick('Get Started For Free', 'testimonials-section');
    openSignupModal('Home Page CTA');
  };

  return (
    <section className="py-20 bg-white">
      <div className="max-w-7xl mx-auto px-4 sm:px-6 lg:px-8">
        <div className="text-center max-w-3xl mx-auto mb-16">
          <motion.span 
            className="text-primary-600 font-semibold tracking-wide uppercase text-sm"
            initial={{ opacity: 0 }}
            whileInView={{ opacity: 1 }}
            viewport={{ once: true }}
          >
            Success Stories
          </motion.span>
          <motion.h2 
            className="mt-2 text-3xl sm:text-4xl font-bold text-gray-900"
            initial={{ opacity: 0, y: 20 }}
            whileInView={{ opacity: 1, y: 0 }}
            viewport={{ once: true }}
            transition={{ delay: 0.1 }}
          >
            What Our Customers Say
          </motion.h2>
          <motion.p 
            className="mt-4 text-xl text-gray-600"
            initial={{ opacity: 0, y: 20 }}
            whileInView={{ opacity: 1, y: 0 }}
            viewport={{ once: true }}
            transition={{ delay: 0.2 }}
          >
            Thousands of teams are using AgentSpot to automate workflows with AI.
          </motion.p>
        </div>
        
        <div className="grid grid-cols-1 md:grid-cols-3 gap-8">
          {testimonials.map((testimonial, index) => (
            <motion.div 
              key={index} 
              className="bg-gray-50 rounded-2xl p-8 relative"
              initial={{ opacity: 0, y: 30 }}
              whileInView={{ opacity: 1, y: 0 }}
              viewport={{ once: true }}
              transition={{ delay: index * 0.1 + 0.3 }}
              whileHover={{ y: -5, boxShadow: "0 10px 25px -5px rgba(0, 0, 0, 0.1)" }}
            >
              <div className="absolute -top-6 left-8">
                <span className="text-6xl text-primary-400">"</span>
              </div>
              <div className="pt-4 relative z-10">
                <p className="text-gray-700 italic mb-6">
                  {testimonial.quote}
                </p>
                <div className="flex items-center">
                  <div className="h-12 w-12 rounded-full bg-gray-300 mr-4"></div>
                  <div>
                    <h4 className="font-semibold text-gray-900">{testimonial.author}</h4>
                    <p className="text-gray-600 text-sm">{testimonial.title}</p>
                  </div>
                </div>
              </div>
            </motion.div>
          ))}
        </div>
        
        <motion.div 
          className="mt-16 bg-gradient-to-r from-primary-600 to-secondary-600 rounded-2xl overflow-hidden shadow-xl"
          initial={{ opacity: 0, y: 30 }}
          whileInView={{ opacity: 1, y: 0 }}
          viewport={{ once: true }}
          transition={{ delay: 0.6 }}
        >
          <div className="px-8 py-12 md:py-16 md:px-16 text-center text-white">
            <h3 className="text-2xl md:text-3xl font-bold mb-4">Ready to transform your workflow?</h3>
            <p className="mb-8 text-lg text-white/80 max-w-2xl mx-auto">
              Join thousands of companies using AgentSpot to deploy AI agents across their tools and platforms.
            </p>
            <motion.button 
              className="bg-white text-primary-600 px-8 py-3 rounded-lg font-semibold text-lg shadow-md"
              whileHover={{ scale: 1.05 }}
              whileTap={{ scale: 0.95 }}
              onClick={handleGetStarted}
            >
              Get Started For Free
            </motion.button>
          </div>
        </motion.div>
      </div>
    </section>
  );
};

export default TestimonialsSection;